import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Phone, MessageCircle, Instagram as IgIcon, ClipboardCopy, Check, Search,
  ChevronDown, ChevronUp, StickyNote, XOctagon, Video,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const AUTH_KEY = 'hda_setter_ok';

// ==================================================================
// Small primitives (all mobile-first, sized for a thumb at 390px wide)
// ==================================================================

const Green = {
  bg: '#0D1F17',
  bg2: '#122A1F',
  card: '#1B3627',
  cardBorder: 'rgba(245, 239, 227, 0.10)',
  text: '#F5EFE3',
  muted: 'rgba(245, 239, 227, 0.55)',
  accent: '#8FD4A8',
  pill: '#2A5F44',
};

const Section = ({ title, right, children, testId }) => (
  <section data-testid={testId} style={{ marginBottom: 20 }}>
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10, gap: 10,
      }}
    >
      <h2
        style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 13,
          color: Green.muted, letterSpacing: '0.16em', textTransform: 'uppercase',
        }}
      >
        {title}
      </h2>
      {right}
    </div>
    {children}
  </section>
);

const EmptyRow = ({ label }) => (
  <div
    style={{
      padding: 14, borderRadius: 12,
      background: 'rgba(245, 239, 227, 0.04)',
      border: '1px dashed rgba(245, 239, 227, 0.12)',
      color: Green.muted, fontSize: 14, textAlign: 'center',
    }}
  >
    {label}
  </div>
);

// ==================================================================
// Date helpers
// ==================================================================

const FRENCH_WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const FRENCH_MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const formatBookingDateFr = (date_str, time_str) => {
  try {
    const [y, m, d] = date_str.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return `${FRENCH_WEEKDAYS[dt.getDay()]} ${dt.getDate()} ${FRENCH_MONTHS[dt.getMonth()]} · ${time_str}`;
  } catch { return `${date_str} ${time_str}`; }
};

const bookingCountdown = (date_str, time_str) => {
  try {
    const [y, m, d] = date_str.split('-').map(Number);
    const [hh, mm] = time_str.split(':').map(Number);
    const target = new Date(y, m - 1, d, hh, mm);
    const diffMs = target.getTime() - Date.now();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const targetMidnight = new Date(y, m - 1, d).setHours(0, 0, 0, 0);
    const todayMidnight = new Date().setHours(0, 0, 0, 0);
    const dayDiff = Math.round((targetMidnight - todayMidnight) / (1000 * 60 * 60 * 24));
    if (dayDiff === 0) return "aujourd'hui";
    if (dayDiff === 1) return 'demain';
    if (dayDiff > 1) return `dans ${dayDiff} jours`;
    if (dayDiff === -1) return 'hier';
    if (diffDays < 0) return `il y a ${Math.abs(dayDiff)} jours`;
    return '';
  } catch { return ''; }
};

const timeSince = (isoStr) => {
  if (!isoStr) return '';
  try {
    const then = new Date(isoStr).getTime();
    const diffMin = Math.floor((Date.now() - then) / 60000);
    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `il y a ${diffH} h`;
    const diffD = Math.floor(diffH / 24);
    return `il y a ${diffD} j`;
  } catch { return ''; }
};

const isSameLocalDate = (date_str, refDate) => {
  try {
    const [y, m, d] = date_str.split('-').map(Number);
    return y === refDate.getFullYear() && m - 1 === refDate.getMonth() && d === refDate.getDate();
  } catch { return false; }
};

const cleanPhoneForWa = (phone) => (phone || '').replace(/[^0-9]/g, '');
const cleanIg = (ig) => (ig || '').replace(/^@+/, '').trim();

const SITUATION_LABELS = {
  zero_ready: 'Part de zéro, prêt à investir',
  under_1k: 'Business < 1 000€/mois',
  '1k_5k': 'Business 1 000 – 5 000€/mois',
  zero: 'Part de zéro',
};

// ==================================================================
// LOGIN
// ==================================================================

const LoginGate = ({ onOk }) => {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const res = await fetch(`${API}/api/setter/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (!res.ok) { setErr('Mot de passe incorrect'); return; }
      localStorage.setItem(AUTH_KEY, '1');
      onOk();
    } catch { setErr('Erreur réseau'); }
    finally { setLoading(false); }
  };

  return (
    <div
      style={{ minHeight: '100vh', background: Green.bg, color: Green.text, padding: '48px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 360 }} data-testid="setter-login-form">
        <h1 style={{ fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em', marginBottom: 8 }}>
          Espace Setter
        </h1>
        <p style={{ color: Green.muted, fontSize: 14, marginBottom: 20 }}>
          Ton mot de passe pour accéder à l&rsquo;espace.
        </p>
        <input
          data-testid="setter-password-input"
          type="password" value={pw} onChange={(e) => setPw(e.target.value)}
          placeholder="Mot de passe" autoFocus
          style={{
            width: '100%', minHeight: 56, padding: '16px 18px',
            background: '#1F4A34', border: '1px solid rgba(245,239,227,0.15)',
            borderRadius: 12, color: Green.text, fontSize: 16, outline: 'none',
            marginBottom: 12,
          }}
        />
        {err && <p data-testid="setter-login-error" style={{ color: '#FF9090', fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <button
          type="submit" disabled={loading || !pw}
          data-testid="setter-login-submit"
          style={{
            width: '100%', minHeight: 56, borderRadius: 12,
            background: '#2A5F44', color: Green.text, fontWeight: 700, fontSize: 16,
            border: 'none', cursor: 'pointer', opacity: loading || !pw ? 0.5 : 1,
          }}
        >
          {loading ? 'Connexion…' : 'Entrer'}
        </button>
      </form>
    </div>
  );
};

// ==================================================================
// LEAD CARD
// ==================================================================

const CHECKLIST = [
  { key: 'called',     label: 'Appelé' },
  { key: 'confirmed',  label: 'RDV confirmé par le prospect' },
  { key: 'video_sent', label: 'Vidéo étude de cas envoyée' },
  { key: 'reminded',   label: 'Relancé la veille' },
];

const ActionButton = ({ href, icon: Icon, label, testId }) => (
  <a
    href={href} target="_blank" rel="noreferrer" data-testid={testId}
    style={{
      flex: 1, minHeight: 52, borderRadius: 12,
      background: Green.pill, color: Green.text,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      textDecoration: 'none', fontSize: 15, fontWeight: 600,
      border: '1px solid rgba(245,239,227,0.12)',
    }}
  >
    <Icon size={18} /> {label}
  </a>
);

const ChecklistRow = ({ item, state, onToggle, disabled }) => {
  const done = !!state?.done;
  return (
    <button
      type="button" onClick={() => onToggle(item.key, !done)} disabled={disabled}
      data-testid={`checklist-${item.key}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        minHeight: 52, width: '100%', padding: '10px 12px', borderRadius: 12,
        background: done ? 'rgba(143,212,168,0.10)' : 'rgba(245,239,227,0.04)',
        border: `1px solid ${done ? 'rgba(143,212,168,0.35)' : 'rgba(245,239,227,0.10)'}`,
        color: Green.text, cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span
        style={{
          width: 26, height: 26, borderRadius: 6, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: done ? Green.accent : 'transparent',
          border: `2px solid ${done ? Green.accent : 'rgba(245,239,227,0.35)'}`,
          color: '#0D1F17',
        }}
      >
        {done && <Check size={16} strokeWidth={3} />}
      </span>
      <span style={{ flex: 1, fontSize: 15, fontWeight: done ? 600 : 500 }}>{item.label}</span>
      {done && state?.ts && (
        <span style={{ color: Green.muted, fontSize: 11 }}>{timeSince(state.ts)}</span>
      )}
    </button>
  );
};

const LeadCard = ({ lead, onCheck, onNoResponse, onAddNote, archived }) => {
  const [open, setOpen] = useState(!archived);
  const [noteText, setNoteText] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [meetCopied, setMeetCopied] = useState(false);
  const ready = CHECKLIST.every((it) => lead.checklist?.[it.key]?.done);
  const collapsed = ready && !archived;

  const copyMeet = async () => {
    if (!lead.meet_url) return;
    try {
      await navigator.clipboard.writeText(lead.meet_url);
      setMeetCopied(true);
      setTimeout(() => setMeetCopied(false), 1600);
    } catch { /* silent */ }
  };

  const situation = SITUATION_LABELS[lead.situation] || lead.situation || '—';
  const countdown = bookingCountdown(lead.booking_date, lead.booking_time);
  const bookingDateLabel = formatBookingDateFr(lead.booking_date, lead.booking_time);

  const submitNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    await onAddNote(lead.id, t);
    setNoteText('');
  };

  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      style={{
        background: Green.card, border: `1px solid ${Green.cardBorder}`,
        borderRadius: 14, padding: 14, marginBottom: 12, color: Green.text,
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
            {lead.first_name} {lead.last_name}
          </p>
          <p style={{ fontSize: 14, marginTop: 4, fontWeight: 700 }}>
            {bookingDateLabel}
            {countdown && (
              <span style={{ color: Green.accent, fontWeight: 600, marginLeft: 8 }}>· {countdown}</span>
            )}
          </p>
          <p style={{ color: Green.muted, fontSize: 13, marginTop: 4 }}>
            {situation}
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {lead.utm_source && (
              <span
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 999,
                  background: 'rgba(143,212,168,0.10)', color: Green.accent,
                  border: '1px solid rgba(143,212,168,0.25)',
                }}
              >
                source · {lead.utm_source}
              </span>
            )}
            {ready && (
              <span
                data-testid={`ready-badge-${lead.id}`}
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 999,
                  background: Green.accent, color: '#0D1F17', fontWeight: 700,
                }}
              >
                Prêt
              </span>
            )}
            {lead.no_response && (
              <span
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 999,
                  background: 'rgba(245,239,227,0.10)', color: Green.muted,
                }}
              >
                Ne répond plus
              </span>
            )}
          </div>
          {!archived && (
            <p style={{ color: Green.muted, fontSize: 11, marginTop: 6 }}>
              Réservé {timeSince(lead.created_at)}
            </p>
          )}
        </div>
        {collapsed && (
          <button
            type="button" onClick={() => setOpen((o) => !o)} aria-label="Détails"
            data-testid={`expand-${lead.id}`}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(245,239,227,0.06)', color: Green.text,
              border: 'none', cursor: 'pointer',
            }}
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </div>

      {(!collapsed || open) && (
        <>
          {/* Action buttons (Call / WhatsApp / IG) */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {lead.phone && (
              <ActionButton href={`tel:${lead.phone}`} icon={Phone} label="Appeler" testId={`btn-call-${lead.id}`} />
            )}
            {lead.phone && (
              <ActionButton href={`https://wa.me/${cleanPhoneForWa(lead.phone)}`} icon={MessageCircle} label="WhatsApp" testId={`btn-wa-${lead.id}`} />
            )}
            {lead.instagram && (
              <ActionButton href={`https://instagram.com/${cleanIg(lead.instagram)}`} icon={IgIcon} label="IG" testId={`btn-ig-${lead.id}`} />
            )}
          </div>

          {/* Copy Google Meet link */}
          {lead.meet_url && (
            <button
              type="button"
              onClick={copyMeet}
              data-testid={`btn-copy-meet-${lead.id}`}
              style={{
                marginTop: 8, width: '100%', minHeight: 44, borderRadius: 10,
                background: meetCopied ? 'rgba(143,212,168,0.15)' : 'rgba(245,239,227,0.04)',
                border: `1px solid ${meetCopied ? 'rgba(143,212,168,0.40)' : 'rgba(245,239,227,0.10)'}`,
                color: Green.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {meetCopied ? <Check size={16} color={Green.accent} /> : <Video size={16} />}
              {meetCopied ? 'Lien Meet copié' : 'Copier le lien Google Meet'}
            </button>
          )}

          {/* Checklist */}
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {CHECKLIST.map((item) => (
              <ChecklistRow
                key={item.key} item={item}
                state={lead.checklist?.[item.key]}
                onToggle={(k, v) => onCheck(lead.id, k, v)}
              />
            ))}
          </div>

          {/* No-response toggle */}
          {!archived && (
            <button
              type="button"
              onClick={() => onNoResponse(lead.id, !lead.no_response)}
              data-testid={`btn-no-response-${lead.id}`}
              style={{
                marginTop: 10, width: '100%', minHeight: 44, borderRadius: 12,
                background: lead.no_response ? 'rgba(255,144,144,0.12)' : 'transparent',
                border: '1px solid rgba(255,144,144,0.28)',
                color: '#FF9090', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <XOctagon size={14} />
              {lead.no_response ? 'Réactiver (répond à nouveau)' : 'Ne répond plus'}
            </button>
          )}

          {/* Notes */}
          <div style={{ marginTop: 12 }}>
            <button
              type="button" onClick={() => setShowNotes((s) => !s)}
              data-testid={`toggle-notes-${lead.id}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'transparent', color: Green.muted, border: 'none',
                cursor: 'pointer', fontSize: 12,
              }}
            >
              <StickyNote size={14} /> Notes ({(lead.notes_log || []).length})
              {showNotes ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showNotes && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflow: 'auto' }}>
                  {(lead.notes_log || []).slice().reverse().map((n, idx) => (
                    <div key={idx} style={{ background: 'rgba(245,239,227,0.04)', padding: 8, borderRadius: 8 }}>
                      <p style={{ fontSize: 13 }}>{n.text}</p>
                      <p style={{ fontSize: 11, color: Green.muted, marginTop: 4 }}>
                        {new Date(n.date).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <input
                    value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Ajouter une note…"
                    data-testid={`note-input-${lead.id}`}
                    style={{
                      flex: 1, minHeight: 44, padding: '10px 12px', borderRadius: 10,
                      background: '#1F4A34', border: '1px solid rgba(245,239,227,0.15)',
                      color: Green.text, fontSize: 14,
                    }}
                  />
                  <button
                    type="button" onClick={submitNote} disabled={!noteText.trim()}
                    data-testid={`note-save-${lead.id}`}
                    style={{
                      minHeight: 44, padding: '0 14px', borderRadius: 10,
                      background: Green.pill, color: Green.text, border: 'none', fontWeight: 600,
                      cursor: 'pointer', opacity: noteText.trim() ? 1 : 0.5,
                    }}
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ==================================================================
// COMPACT ACTION-BLOCK ROW (À APPELER / RELANCE J-1)
// ==================================================================

const ActionBlockRow = ({ lead, hint }) => (
  <div
    data-testid={`action-row-${lead.id}`}
    style={{
      background: 'rgba(143,212,168,0.06)',
      border: '1px solid rgba(143,212,168,0.22)',
      padding: 12, borderRadius: 12, marginBottom: 8,
      display: 'flex', alignItems: 'center', gap: 10,
    }}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ fontSize: 15, fontWeight: 700, color: Green.text }}>
        {lead.first_name} {lead.last_name}
      </p>
      <p style={{ fontSize: 12, color: Green.muted, marginTop: 2 }}>
        {hint}
      </p>
    </div>
    {lead.phone && (
      <a
        href={`tel:${lead.phone}`}
        data-testid={`action-call-${lead.id}`}
        style={{
          minHeight: 44, minWidth: 44, borderRadius: 10, background: Green.accent, color: '#0D1F17',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Phone size={18} />
      </a>
    )}
    {lead.phone && (
      <a
        href={`https://wa.me/${cleanPhoneForWa(lead.phone)}`} target="_blank" rel="noreferrer"
        data-testid={`action-wa-${lead.id}`}
        style={{
          minHeight: 44, minWidth: 44, borderRadius: 10, background: Green.pill, color: Green.text,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <MessageCircle size={18} />
      </a>
    )}
  </div>
);

// ==================================================================
// MAIN PAGE
// ==================================================================

export const SetterPage = () => {
  const [authed, setAuthed] = useState(() => localStorage.getItem(AUTH_KEY) === '1');
  const [active, setActive] = useState([]);
  const [archives, setArchives] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState('todo'); // todo | ready | no_response | all | archives
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, videoRes] = await Promise.all([
        fetch(`${API}/api/setter/leads`),
        fetch(`${API}/api/settings/case-study-video`),
      ]);
      const leads = await leadsRes.json();
      const video = await videoRes.json();
      setActive(leads.active || []);
      setArchives(leads.archives || []);
      setVideoUrl(video.value || '');
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  // Derived lists
  const toCall = useMemo(
    () => active.filter((l) => !l.no_response && !l.checklist?.called?.done),
    [active]
  );
  const tomorrow = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d;
  }, []);
  const remindJm1 = useMemo(
    () => active.filter((l) =>
      !l.no_response &&
      isSameLocalDate(l.booking_date, tomorrow) &&
      !l.checklist?.reminded?.done
    ),
    [active, tomorrow]
  );

  const filtered = useMemo(() => {
    const src = filter === 'archives' ? archives : active;
    const q = search.trim().toLowerCase();
    let list = src;
    if (filter === 'todo') list = active.filter((l) =>
      !l.no_response && !CHECKLIST.every((it) => l.checklist?.[it.key]?.done)
    );
    else if (filter === 'ready') list = active.filter((l) =>
      CHECKLIST.every((it) => l.checklist?.[it.key]?.done)
    );
    else if (filter === 'no_response') list = active.filter((l) => l.no_response);
    if (q) {
      list = list.filter((l) => {
        const hay = `${l.first_name || ''} ${l.last_name || ''} ${l.email || ''} ${l.instagram || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }
    // Sort by booking date ascending for active; descending for archives
    return [...list].sort((a, b) => {
      const av = `${a.booking_date} ${a.booking_time}`;
      const bv = `${b.booking_date} ${b.booking_time}`;
      return filter === 'archives' ? (bv < av ? -1 : 1) : (av < bv ? -1 : 1);
    });
  }, [filter, active, archives, search]);

  const onCheck = async (lead_id, key, done) => {
    // Optimistic update
    const ts = done ? new Date().toISOString() : null;
    setActive((list) => list.map((l) =>
      l.id === lead_id
        ? { ...l, checklist: { ...(l.checklist || {}), [key]: { done, ts } } }
        : l
    ));
    try {
      await fetch(`${API}/api/setter/leads/${lead_id}/checklist`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, done }),
      });
    } catch { load(); }
  };

  const onNoResponse = async (lead_id, value) => {
    setActive((list) => list.map((l) =>
      l.id === lead_id ? { ...l, no_response: value } : l
    ));
    try {
      await fetch(`${API}/api/setter/leads/${lead_id}/no-response`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ no_response: value }),
      });
    } catch { load(); }
  };

  const onAddNote = async (lead_id, text) => {
    try {
      const res = await fetch(`${API}/api/setter/leads/${lead_id}/notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (res.ok) {
        const data = await res.json();
        const updater = (list) => list.map((l) =>
          l.id === lead_id
            ? { ...l, notes_log: [...(l.notes_log || []), data.entry] }
            : l
        );
        setActive(updater); setArchives(updater);
      }
    } catch { /* silent */ }
  };

  const copyVideo = async () => {
    try {
      await navigator.clipboard.writeText(videoUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* silent */ }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_KEY);
    setAuthed(false);
  };

  if (!authed) return <LoginGate onOk={() => setAuthed(true)} />;

  return (
    <div
      data-testid="setter-page"
      style={{ minHeight: '100vh', background: Green.bg, color: Green.text }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 80px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 8 }}>
          <h1 style={{ fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em' }}>
            Espace Setter
          </h1>
          <button
            type="button" onClick={logout} data-testid="setter-logout"
            style={{
              padding: '8px 12px', borderRadius: 10, fontSize: 12,
              background: 'rgba(245,239,227,0.06)', color: Green.muted,
              border: '1px solid rgba(245,239,227,0.10)', cursor: 'pointer',
            }}
          >
            Déconnexion
          </button>
        </div>

        {/* Copy case study video */}
        <button
          type="button" onClick={copyVideo}
          data-testid="copy-video-btn"
          style={{
            width: '100%', minHeight: 52, borderRadius: 12, marginBottom: 16,
            background: copied ? 'rgba(143,212,168,0.15)' : Green.card,
            border: `1px solid ${copied ? 'rgba(143,212,168,0.40)' : Green.cardBorder}`,
            color: Green.text, fontSize: 15, fontWeight: 600, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}
        >
          {copied ? <Check size={18} color={Green.accent} /> : <ClipboardCopy size={18} />}
          {copied ? 'Lien copié' : 'Copier le lien de la vidéo étude de cas'}
        </button>

        {/* Action blocks */}
        <Section title="À appeler" testId="block-to-call">
          {toCall.length === 0
            ? <EmptyRow label="Rien à faire ici" />
            : toCall.map((l) => (
              <ActionBlockRow
                key={l.id} lead={l}
                hint={`Réservé ${timeSince(l.created_at)}`}
              />
            ))}
        </Section>

        <Section title="Relance J-1" testId="block-remind">
          {remindJm1.length === 0
            ? <EmptyRow label="Rien à faire ici" />
            : remindJm1.map((l) => (
              <ActionBlockRow
                key={l.id} lead={l}
                hint={`Demain à ${l.booking_time}`}
              />
            ))}
        </Section>

        {/* Filters + search */}
        <div
          style={{
            display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 10,
          }}
          data-testid="filter-bar"
        >
          {[
            ['todo', 'À traiter'],
            ['ready', 'Prêts'],
            ['no_response', 'Ne répond plus'],
            ['all', 'Tous'],
            ['archives', 'Anciens leads'],
          ].map(([k, label]) => (
            <button
              key={k} onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              style={{
                padding: '8px 12px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap',
                background: filter === k ? Green.accent : 'rgba(245,239,227,0.06)',
                color: filter === k ? '#0D1F17' : Green.text,
                border: '1px solid rgba(245,239,227,0.10)', cursor: 'pointer', fontWeight: 600,
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative', marginBottom: 14 }}>
          <Search size={16} style={{ position: 'absolute', top: 18, left: 14, color: Green.muted }} />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un nom, un email, un IG…"
            data-testid="setter-search"
            style={{
              width: '100%', minHeight: 48, padding: '10px 12px 10px 40px',
              background: '#1F4A34', border: '1px solid rgba(245,239,227,0.10)',
              borderRadius: 12, color: Green.text, fontSize: 14, outline: 'none',
            }}
          />
        </div>

        {/* Lead list */}
        <Section title={filter === 'archives' ? 'Anciens leads' : 'Rendez-vous à venir'} testId="lead-list">
          {loading && filtered.length === 0 && (
            <p style={{ color: Green.muted, fontSize: 13, textAlign: 'center' }}>Chargement…</p>
          )}
          {!loading && filtered.length === 0 && (
            <EmptyRow label={filter === 'archives' ? 'Aucun ancien lead' : 'Aucun rendez-vous dans cette liste'} />
          )}
          {filtered.map((l) => (
            <LeadCard
              key={l.id} lead={l} archived={filter === 'archives'}
              onCheck={onCheck} onNoResponse={onNoResponse} onAddNote={onAddNote}
            />
          ))}
        </Section>
      </div>
    </div>
  );
};

export default SetterPage;
